import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import ActionsButton from '@common/components/ActionsButton';
import { getTestAttributes } from '@common/utils/tests';
import { Table, TABLE_COLUMN_PREDEFINED_ACTION_TYPE } from '@common/components/Table';
import { MODULE_NAME } from '../../individuals';
import { getFields, getFolders, sendInvite, validateInvitesEmail } from '../../actions/individuals';
import { ROOT_SYSTEM_FIELDS_ID } from '../../constants/folders';
import { CONTEXT_TYPE } from '../../constants/context';
import { NewInvite, NEW_INVITE_STEPS } from '../new-invite';

import './invites-table.styles.scss';

export class InvitesTable extends React.Component {
  featureType = {
    setDefaultForAllUsers: 1,
    setDefaultForSpecificUsers: 2,
    applyCurrentColumnsToAllUsers: 3,
    applyCurrentColumnsToSpecificProfiles: 4,
    restoreColumnsToDefault: 5,
    addColumns: 6,
    deleteColumn: 7
  };

  state = {
    allowedFeature: {},
    batchNewInviteProps: {
      opened: false,
    },
  };

  get columns() {
    const defaultWidth = 150;

    return [
      { 
        disableActions: true,
        resizable: false,
        sortable: false,
        Header: 'Actions', 
        accessor: 'ACTIONS_COLUMNS_LOCAL',
        Cell: ({ original }) => (
          <ActionsButton 
            id={original.id} 
            data={original} 
            menuItems={this.actions}
            setCurrent={this.handleSetCurrentSelectedRow}
          />
        ),
        minWidth: 65,
        width: 65,
        className: 'table-actions-cell'
      },
      ...this.props.columns.map((x) => ({
        accessor: x.field.id.toString(),
        sortable: true,
        minWidth: x.width || defaultWidth,
        width: x.width || defaultWidth,
        Header: x.field.name,
      }))
    ]
  }

  componentDidMount() {
    this.props.getAllowedFeatures().then((allowedFeature) => {
      this.setState((x) => ({ ...x, allowedFeature }));
    });
  }

  handleSetCurrentSelectedRow = (id) => {
    this.props.onSelectedInvitesChange([id]);
  }

  getTrProps = (state, { index }) => getTestAttributes(MODULE_NAME, 'invite', 'table-row', index);

  getTheadThProps = (tableData, rowData, { id }) => getTestAttributes(MODULE_NAME, 'invite', 'table-header-cell', id);

  getTdProps = (tableData, { index }, { id }) => getTestAttributes(MODULE_NAME, 'invite', 'table-cell', `${index}-${id}`);

  handleColumnResize = (columns) => {
    const processedColumns = columns.reduce((acc, { id, value }) => {
      acc[id] = Math.round(value);

      return acc;
    }, {});

    this.props.onResize(processedColumns);
  }

  handleNewInviteOpen = () => {
    this.props.onNewInviteOpen();
  }

  handleQuestionnaireOpen = () => {
    this.props.onQuestionnaireOpen({
      name: I18n.t('patient'),
    });
  }

  handleReminderOpen = (x) => {
    this.props.onReminderOpen({
      id: x,
    });
  }

  get actions() {
    return [
      { label: 'New Invite', onClick: this.handleNewInviteOpen, },
      { label: 'Open Questionnaire', onClick: this.handleQuestionnaireOpen, },
      { label: 'Send Reminder', onClick: this.handleReminderOpen, },
      { label: 'Open Datasheet', onClick: this.props.onDatasheetOpen, },
      { label: 'Open Pedigree', onClick: this.props.onPedigreeOpen, },
      { label: 'Generate Letter', onClick: this.props.onGenerateLetter, },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }

  handleSelectedItemsChange = (allSelectedITems) => {
    const { onSelectedInvitesChange } = this.props;

    onSelectedInvitesChange(allSelectedITems.map((x) => x.id));
  }

  get columnActions() {
    return [
      { feature: this.featureType.addColumns, type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn },
      { feature: this.featureType.deleteColumn, type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.deleteColumn },
    ].filter(({ feature }) => feature ? this.state.allowedFeature[feature] : true).map(({ feature, ...otherProps }) => otherProps);;
  }

  get headerActions() {
    return [
      { feature: this.featureType.addColumns, type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn },
    ].filter(({ feature }) => feature ? this.state.allowedFeature[feature] : true).map(({ feature, ...otherProps }) => otherProps);;
  }

  handleColumnAdd = ({ field, folder }, { columnIndex }) => {
    const isSystemRootFolder = ROOT_SYSTEM_FIELDS_ID === folder.id; 

    const column = {
      width: 100,
      field: {
        id: field.fieldId,
        name: field.fieldName,
        context: isSystemRootFolder ? CONTEXT_TYPE.invites : CONTEXT_TYPE.individual,
        type: field.fieldTypeId,
        system: field.systemField
      },
      sortOrder: 0,
      sortAsc: undefined

    };
    const columnsCopy = [...this.props.columns];
    
    columnsCopy.splice(columnIndex, 0, column);
    
    return this.props.onColumnAdd(columnsCopy);
  }

  handleColumnRemove = ({ columnIndex }) => {
    const columnsCopy = [...this.props.columns];
    const actionsColumn = 1;

    columnsCopy.splice(columnIndex - actionsColumn, 1);

    return this.props.onColumnRemove(columnsCopy);
  }

  handleGetFields = ({ searchKeyword, folderId, system }) => {
    const noFolderSelected = 0;
    const isSystemRootFolder = ROOT_SYSTEM_FIELDS_ID === folderId;

    return getFields({
      searchQuery: searchKeyword,
      folderId: folderId || noFolderSelected,
      system,
      contextId: isSystemRootFolder ? CONTEXT_TYPE.invites : CONTEXT_TYPE.individual
    });
  }

  closeBatchNewInvites = () => {
    this.setState((x) => ({
      ...x,
      batchNewInviteProps: {
        opened: false,
      },
    }));
  }

  openBatchNewInvites = ({ data }) => {
    if (data?.selectedItems?.length) {
      this.setState((x) => ({
        ...x,
        batchNewInviteProps: {
          opened: true,
        },
      }));
    }
  }

  sendBatchNewInvites = async (invite) => {
    const result = await validateInvitesEmail(invite.wdcfolderId, this.props.individualId);

    if (result && result.code !== 200) {
      const error = result.errors && result.errors[0] || I18n.t("Email appears to be invalid");

      throw {
        entity: error
      };
    }
    
    await sendInvite(invite);
    this.props.showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
  }

  get rowContextMenuActions() {
    return [
      {
        label: I18n.t('Batch Letters'),
        onClick: this.props.onGenerateLetter,
      },
      {
        label: I18n.t('New Invites'),
        onClick: this.openBatchNewInvites,
      }
    ];
  }

  getBatchNewInvitesSteps = () => {
    return [
      {
        condition: true,
        step: NEW_INVITE_STEPS.emailTemplate,
      }
    ];
  }

  render() {
    const { className, totalCount, getData, data, loadMetadata, setLoadMetadata, individualSelected, selectedInvitesId, individualId, showErrorMessage } = this.props;

    return (
      <div className={cx('InvitesTable', className)}>
        {this.state.batchNewInviteProps.opened && (
          <NewInvite
            individualId={individualId}
            showErrorMessage={showErrorMessage}
            getSteps={this.getBatchNewInvitesSteps}
            sendInvite={this.sendBatchNewInvites}
            onSubmit={this.closeBatchNewInvites}
            onClose={this.closeBatchNewInvites}
          />
        )}
        <Table
          disableAutoInit
          infiniteScroll
          serverSorting
          noDataMessage={individualSelected ? I18n.t('There are no invites to display') : I18n.t('Select an individual to view invites')}
          moduleName={`${MODULE_NAME}_invites_table`}
          config={{
            data,
            columns: this.columns,
            getTrProps: this.getTrProps,
            getTheadThProps: this.getTheadThProps,
            getTdProps: this.getTdProps,
            onResizedChange: debounce(this.handleColumnResize, 500)
          }}
          columnsManagement={{
            refreshDataOnChange: false,
          }}
          idProperty="id"
          itemsCount={totalCount}
          onLoadData={getData}
          loadMetadata={loadMetadata}
          setLoadMetadata={setLoadMetadata}
          onColumnAdd={this.handleColumnAdd}
          onColumnRemove={this.handleColumnRemove}
          getFolders={getFolders}
          getFields={this.handleGetFields}
          columnActions={this.columnActions}
          headerActions={this.headerActions}
          selectedItems={selectedInvitesId.map((id) => ({ id }))}
          rowContextMenuActions={this.rowContextMenuActions}
          onSelectedItemsChange={this.handleSelectedItemsChange}
        />
      </div>
    );
  }
}